import { useQuery } from '@tanstack/react-query';
import { Button, Callout, Card, CardBody, CardHeader, PageHeader, Skeleton } from '@bifeai/ui';
import { MessageCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { BifiLineQuoteDTO } from '@bifeai/shared';
import { Link } from 'react-router';
import { api, errorMessage } from '../../lib/api';
import { formatUsd } from '../../lib/format';

interface MetaConfig {
  enabled: boolean;
  appId: string | null;
  configId: string | null;
  graphVersion: string;
}
interface SignupData {
  phone_number_id?: string;
  waba_id?: string;
  business_id?: string;
}
type FbLoginResponse = { authResponse?: { code?: string } | null };
declare global {
  interface Window {
    FB?: {
      init(o: Record<string, unknown>): void;
      login(cb: (r: FbLoginResponse) => void, o: Record<string, unknown>): void;
    };
    fbAsyncInit?: () => void;
  }
}

/** Carga el SDK de Facebook una sola vez (esta página tiene su propia política de seguridad que lo permite). */
function loadSdk(cfg: MetaConfig): Promise<void> {
  if (window.FB) return Promise.resolve();
  return new Promise((resolve, reject) => {
    window.fbAsyncInit = () => {
      window.FB!.init({ appId: cfg.appId, autoLogAppEvents: true, xfbml: false, version: cfg.graphVersion });
      resolve();
    };
    const s = document.createElement('script');
    s.src = 'https://connect.facebook.net/es_LA/sdk.js';
    s.async = true;
    s.defer = true;
    s.crossOrigin = 'anonymous';
    s.onerror = () => reject(new Error('No se pudo cargar Facebook. Revisá la conexión o un bloqueador de anuncios.'));
    document.body.appendChild(s);
  });
}

/**
 * Conectar el WhatsApp de la carnicería con el registro integrado de Meta. Se abre una ventana de Facebook donde la
 * carnicería elige (o crea) su cuenta de empresa y su número. Al terminar, BifeAI completa la conexión.
 * Se entra con una carga completa de la página (no navegación interna) para que rija su política de seguridad.
 */
export function ConnectWhatsAppPage() {
  const cfg = useQuery({ queryKey: ['orders-chat', 'whatsapp', 'config'], queryFn: () => api.get<MetaConfig>('/orders-chat/whatsapp/config') });
  const [ready, setReady] = useState(false);
  const [state, setState] = useState<'idle' | 'waiting' | 'saving' | 'done' | 'error'>('idle');
  const [message, setMessage] = useState<string | null>(null);
  const session = useRef<{ data: SignupData; event: string } | null>(null);
  const code = useRef<string | null>(null);
  // bifi se cobra por número: antes de conectar se muestra cuánto cuesta y quién lo paga.
  const quote = useQuery({ queryKey: ['billing', 'bifi-line-quote'], queryFn: () => api.get<BifiLineQuoteDTO>('/billing/bifi/line-quote') });
  const [agree, setAgree] = useState(false);
  const q = quote.data;
  const blocked = !!q?.needsSeat || (!!q && !q.included && !agree);

  useEffect(() => {
    if (!cfg.data?.enabled) return;
    loadSdk(cfg.data)
      .then(() => setReady(true))
      .catch((err: unknown) => {
        setState('error');
        setMessage(err instanceof Error ? err.message : 'No se pudo cargar Facebook.');
      });
    // Meta manda los ids de la cuenta y del número por un mensaje a esta ventana.
    const onMessage = (event: MessageEvent) => {
      if (!/(^|\.)facebook\.com$/.test(new URL(event.origin).hostname)) return;
      try {
        const data = JSON.parse(String(event.data)) as {
          type?: string;
          event?: string;
          data?: SignupData & { current_step?: string; error_message?: string };
        };
        if (data.type !== 'WA_EMBEDDED_SIGNUP') return;
        if (data.event === 'CANCEL') {
          setState('error');
          setMessage(data.data?.error_message ?? 'Cerraste la ventana antes de terminar. Podés volver a intentarlo.');
          return;
        }
        session.current = { data: data.data ?? {}, event: data.event ?? 'FINISH' };
        void finish();
      } catch {
        // Otros mensajes de Facebook: no son del registro.
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [cfg.data]);

  /** Cuando llegaron el código y los ids (pueden llegar en cualquier orden), se completa la conexión en BifeAI. */
  async function finish() {
    const s = session.current;
    if (!s || !code.current) return;
    if (!s.data.phone_number_id || !s.data.waba_id) {
      setState('error');
      setMessage('Meta no devolvió el número. Si querés usar el número con la app WhatsApp Business, elegí esa opción en la ventana de Facebook.');
      return;
    }
    setState('saving');
    try {
      await api.post('/orders-chat/whatsapp/connect', {
        code: code.current,
        wabaId: s.data.waba_id,
        phoneNumberId: s.data.phone_number_id,
        businessId: s.data.business_id ?? null,
        event: s.event,
      });
      setState('done');
    } catch (err) {
      setState('error');
      setMessage(errorMessage(err));
    }
    code.current = null;
    session.current = null;
  }

  const launch = () => {
    if (!window.FB || !cfg.data?.configId) return;
    setState('waiting');
    setMessage(null);
    window.FB.login(
      (r) => {
        if (r.authResponse?.code) {
          code.current = r.authResponse.code;
          void finish();
        } else {
          setState('error');
          setMessage('No se completó el ingreso con Facebook.');
        }
      },
      { config_id: cfg.data.configId, response_type: 'code', override_default_response_type: true, extras: { setup: {}, sessionInfoVersion: '3' } },
    );
  };

  return (
    <>
      <PageHeader
        title="Conectar WhatsApp"
        subtitle="Conectá el número de WhatsApp de la carnicería para que el agente atienda y los pedidos entren solos."
      />
      <Card>
        <CardHeader title="Antes de empezar" />
        <CardBody>
          <ul style={{ margin: 0, paddingLeft: 18, display: 'grid', gap: 6 }}>
            <li>Necesitás un usuario de Facebook con el que vas a administrar la cuenta de empresa de la carnicería.</li>
            <li>Tené a mano el celular del número: llega un código por SMS o llamada para verificarlo.</li>
            <li>Si el número ya usa la app WhatsApp Business, podés seguir usándola: elegí esa opción en la ventana de Facebook.</li>
            <li>
              Meta le cobra directo a la carnicería: al terminar, cargá una tarjeta en tu cuenta de WhatsApp de Meta. Cada número tiene 1.000
              respuestas gratis por mes.
            </li>
          </ul>
        </CardBody>
      </Card>
      <Card>
        <CardBody>
          {cfg.isPending ? (
            <Skeleton />
          ) : !cfg.data?.enabled ? (
            <Callout tone="warning">La conexión con WhatsApp todavía no está habilitada. Avisale a BifeAI.</Callout>
          ) : state === 'done' ? (
            <div style={{ display: 'grid', gap: 12 }}>
              <Callout tone="success">¡Listo! El WhatsApp quedó conectado. Revisá qué sucursales atiende y cargá la tarjeta en Meta.</Callout>
              <div>
                <Button onClick={() => window.location.assign('/pedidos/configuracion')}>Volver a la configuración</Button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 12, justifyItems: 'start' }}>
              {state === 'error' && message && <Callout tone="danger">{message}</Callout>}
              {q?.needsSeat && (
                <Callout tone="warning">
                  El bifi de tu franquicia lo pagás vos: antes de conectar el número, activá tu abono ({formatUsd(q.unitCents)} por mes por número)
                  desde <Link to="/plan">Mi plan</Link>.
                </Callout>
              )}
              {q && !q.needsSeat && !q.included && (
                <Callout tone="info">
                  bifi se paga por número de WhatsApp. Este número suma {formatUsd(q.unitCents)} por mes
                  {q.payer === 'FRANCHISEE' ? ' a tu abono' : ' al abono de la marca'}
                  {q.amountCents > 0 ? `, y hoy se cobra ${formatUsd(q.amountCents)} (lo proporcional de este mes) con la tarjeta guardada.` : '.'}
                  <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10 }}>
                    <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} /> Entendido, conectar igual
                  </label>
                </Callout>
              )}
              <Button
                icon={<MessageCircle />}
                onClick={launch}
                disabled={!ready || blocked || state === 'waiting' || state === 'saving'}
                loading={state === 'saving'}
              >
                {state === 'saving' ? 'Conectando…' : state === 'waiting' ? 'Seguí en la ventana de Facebook…' : 'Conectar con Facebook'}
              </Button>
              <Button variant="ghost" onClick={() => window.location.assign('/pedidos/configuracion')}>
                Volver
              </Button>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  );
}
